/**
 * Labstep
 *
 * @module prosemirror/components/NodeView/Date
 * @desc Renders a Date node
 */

import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { DateTimePicker } from 'labstep-web/core/DateTimePicker';
import { Icon } from 'labstep-web/core/Icon';
import { useReactNodeView } from 'labstep-web/prosemirror/components/ReactNodeView';
import moment from 'moment';
import styles from './styles.module.scss';
import type { DateNodeProps, DateNodeViewProps } from './types';

const DateNodeDisplay = ({ canEdit, date }: DateNodeProps) => {
  const context = useReactNodeView();

  const handleDateChange = (selectedDate: Date) => {
    if (context && context.view && context.getPos && context.node) {
      const { view, getPos, node } = context;
      if (typeof getPos === 'function') {
        const pos = getPos();

        // Update the node's date attribute
        const tr = view.state.tr.setNodeMarkup(pos, null, {
          ...node.attrs,
          date: moment(selectedDate).format('YYYY-MM-DD'),
        });

        view.dispatch(tr);
      }
    }
  };

  return (
    <div className={styles['date-node']} contentEditable={false}>
      <div>
        <Icon name="calendar alternate" />{' '}
        <DateTimePicker
          value={date}
          disabled={!canEdit}
          onChange={(selectedDates) => {
            handleDateChange(selectedDates[0]);
          }}
          options={{
            inline: false,
            clickOpens: true,
            defaultDate: date,
            enableTime: false,
            dateFormat: 'd M Y',
          }}
        />
      </div>
      <hr />
    </div>
  );
};

export const DateNodeView = ({ entity }: DateNodeViewProps) => {
  const context = useReactNodeView();
  const { date } = (context.node as any).attrs;
  const canEdit = useHasAccess(
    entity.entityName,
    entity.id,
    Action.edit,
  );

  return <DateNodeDisplay canEdit={canEdit} date={new Date(date)} />;
};
